import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import { createShopBroadcast, fetchDashboard, fetchShopPlayers } from '../services/api';
import type { ShopDashboardData, ShopPlayer } from '../types';
import { PAGE } from '@kioskgaming/ui';

const TITLE_MAX = 500;
const BODY_MAX = 1000;

export default function SendBroadcastPage() {
  const [dash, setDash] = useState<ShopDashboardData | null>(null);
  const [players, setPlayers] = useState<ShopPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([fetchDashboard(), fetchShopPlayers()]);
      if (d.success && d.data) setDash(d.data);
      if (p.success && p.data) setPlayers(p.data.filter((u) => u.status === 'active'));
    } catch {
      toast.error('Could not load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dash?.can_delegate_popups) return;
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (selected.size === 0) {
      toast.error('Select at least one Player');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createShopBroadcast({
        title: title.trim(),
        body: body.trim(),
        targetUserIds: Array.from(selected),
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      if (res.success) {
        toast.success('Broadcast sent');
        setTitle('');
        setBody('');
        setExpiresAt('');
        setSelected(new Set());
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout contentFullWidth>
        <PageLoadingIndicator variant="page" accentColor="#059669" />
      </AppLayout>
    );
  }

  if (dash && !dash.can_delegate_popups) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout contentFullWidth>
      <h1 className="text-xl font-semibold text-stone-900 mb-2">{PAGE.broadcasts}</h1>
      <p className="text-sm text-stone-600 mb-6 max-w-xl">
        Send a bell notification broadcast to Players assigned to your Shop. This uses the same permission as{' '}
        {PAGE.popups}.
      </p>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-stone-700">Title</label>
            <span className="text-xs text-stone-500 tabular-nums">
              {title.length}/{TITLE_MAX}
            </span>
          </div>
          <input
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            maxLength={TITLE_MAX}
            required
          />
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-stone-700">Message</label>
            <span className="text-xs text-stone-500 tabular-nums">
              {body.length}/{BODY_MAX}
            </span>
          </div>
          <textarea
            className="w-full min-h-[140px] rounded-lg border border-stone-300 px-3 py-2 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            maxLength={BODY_MAX}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Hide after (optional)</label>
          <input
            type="datetime-local"
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div>
          <p className="text-sm font-medium text-stone-700 mb-2">Players</p>
          {players.length === 0 ? (
            <p className="text-sm text-stone-500">No active Players.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100">
              {players.map((pl) => {
                const label = pl.phone || pl.email || pl.username || pl.id;
                return (
                  <label
                    key={pl.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-stone-50"
                  >
                    <input type="checkbox" checked={selected.has(pl.id)} onChange={() => toggle(pl.id)} />
                    <span className="font-medium text-stone-900 truncate">{label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Preview</p>
          <p className="mt-1 text-sm font-semibold text-stone-900">{title.trim() || 'Notification title'}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-stone-700">
            {body.trim() || 'Your message preview appears here.'}
          </p>
          <p className="mt-2 text-xs text-stone-500">
            Delivery status (queued, sent or failed) is available in the Admin broadcast history.
          </p>
        </div>
        <button
          type="submit"
          disabled={submitting || players.length === 0}
          className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send broadcast'}
        </button>
      </form>
    </AppLayout>
  );
}
